<template>
<div>
    <div
        class="flex items-center gap-8 flex-wrap w-full cursor-text rounded border outline-none py-12 px-16 border-primary-80"
        @click="focusInput"
    >
        <tag
            v-for="(item, index) in value"
            :key="item.id"
            interactive
            icon="cancel"
            icon-after
            class="w-fit"
            @click="toggleSelect(item, index)"
        >
            {{ title(item) }}
        </tag>
        <input
            ref="itemSearch"
            id="item-search-input"
            type="search"
            v-model="filterText"
            :placeholder="getPlaceholder"
            class="text-primary placeholder-primary text-body-sm bg-transparent w-full ring-0 outline-0"
            @focus="toggleFocus(true)"
            @blur="toggleFocus"
            @input="highlightItem()"
            @keydown.up.prevent="highlightPrevious"
            @keydown.down.prevent="highlightNext"
            @keydown.enter.prevent="toggleSelect(filteredItems[highlightedItem])"
            @keydown.delete="handleDelete"
        >
    </div>
    <div
        class="bg-white rounded shadow overflow-y-auto max-h-320 scroll-styled border border-primary mt-8"
        ref="container"
    >
        <ul>
            <li v-for="(item, index) in filteredItems" :key="item.id" :ref="'item-' + index">

                <button
                    @mouseover="highlightItem(index, item)"
                    @click="toggleSelect(item)"
                    class="group px-16 h-40 leading-none flex items-center w-full"
                    :class="cssClass(index, item)"
                >
                    <tooltip
                        v-if="item.disabled && item.tooltip"
                        :key="index"
                        wrapperClass=""
                        :content="item.tooltip"
                        postition="right"
                    >
                        <span class="mr-auto">{{ title(item) }}</span>
                    </tooltip>

                    <template v-else>
                        <span class="mr-auto">{{ title(item) }}</span>
                    </template>

                    <span class="font-semibold"
                          :class="[ highlightedItem === index && !item.disabled ? 'inline' : 'hidden' ]">{{
                            $t('general.select')
                        }}</span>
                </button>
            </li>
        </ul>
    </div>
</div>
</template>

<script>
import _upperFirst from "lodash/upperFirst";
import SimpleButton from "./SimpleButton.vue";
import SvgIcon from "./SvgIcon.vue";
import Loading from "./Loading.vue";
import {mapState} from "pinia";
import {useMainStore} from '../../state/store';
import Tooltip from "../navigation/Tooltip.vue";
import Tag from "./Tag.vue";

export default {
    name: "AutocompleteSelector",
    components: {
        Tag,
        SimpleButton,
        SvgIcon,
        Loading,
        Tooltip
    },
    props: {
        autoFocus: {
            type: Boolean,
            default: true
        },
        currentItems: Array,
        value: null,
        placeholder: String,
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        query() {
            return this.filterText.trim().toLowerCase();
        },
        titles() {
            return this.allItems.map(({title}) => title);
        },
        filteredItems() {
            return this.allItems
                .filter(item => {
                    return !this.value.find(selectedItem => selectedItem.id === item.id) && item.title.toLowerCase().includes(this.query);
                })
                .sort((a, b) => a.title.localeCompare(b.title, this.locale));
        },
        canAddNew() {
            return false;
        },
        getPlaceholder() {
            return `${_upperFirst(this.placeholder || this.$t('general.search'))} ...`;
        },
    },
    data: function () {
        return {
            isFocussed: false,
            allItems: this.currentItems,
            filterText: '',
            highlightedItem: null,
            confirmingClose: false,
        }
    },
    methods: {
        title(item) {
            if (typeof item.title === 'object') {
                return item.title[this.locale] || item.title['en'] || this.$t('general'.no_trans);
            }

            return item.title;
        },
        toggleSelect(item, index = null) {
            if (item.disabled) {
                return;
            }

            if (index !== null) {
                this.value.splice(index, 1);
            } else {
                this.value.push(item);
                this.filterText = '';
                this.setFirstEnabledItem();
                this.$refs.itemSearch.focus();
            }

            this.$emit('input', this.value);
            this.$emit('selected', {
                id: item.id,
                delete: index !== null
            });
        },
        toggleFocus(show = true) {
            this.isFocussed = show;
        },
        setFirstEnabledItem() {
            let firstEnabledItemIndex = null;
            for (const [index, item] of this.filteredItems.entries()) {
                if (!item.disabled) {
                    firstEnabledItemIndex = index;
                    break;
                }
            }

            this.highlightedItem = firstEnabledItemIndex;
        },
        highlightItem(index = null, item = null) {
            if (item && item.disabled) {
                return;
            }

            this.highlightedItem = index !== null ? index : (this.canAddNew ? 'new' : 0);
        },
        highlightPrevious() {
            if (this.highlightedItem === 'new') {
                this.highlightedItem = this.filteredItems.length
                    ? this.filteredItems.length - 1
                    : 'new';
            } else {
                this.highlightedItem = (this.highlightedItem - 1) < 0
                    ? (this.canAddNew ? 'new' : this.filteredItems.length - 1)
                    : this.highlightedItem - 1;
            }

            this.scrollIntoView();
        },
        highlightNext() {
            if (this.highlightedItem === 'new') {
                this.highlightedItem = this.filteredItems.length
                    ? 0
                    : 'new';
            } else {
                this.highlightedItem = (this.highlightedItem + 1) === this.filteredItems.length
                    ? (this.canAddNew ? 'new' : 0)
                    : this.highlightedItem + 1;
            }

            this.scrollIntoView();
        },
        scrollIntoView() {
            let container = this.$refs.container;
            let el = this.$refs['item-' + this.highlightedItem][0] || this.$refs['item-' + this.highlightedItem];

            if (el.offsetTop < container.scrollTop) {
                container.scrollTop = el.offsetTop;
            }

            if (el.offsetTop + el.clientHeight > container.clientHeight + container.scrollTop) {
                container.scrollTop = el.offsetTop - (container.clientHeight - el.clientHeight);
            }
        },
        handleDelete(e) {
            if (e.code !== 'Backspace') {
                return;
            }

            if (!this.filterText.length) {
                this.value.pop();
            }
        },
        handleSubmit() {
            this.$emit('update', this.items);
        },
        focusInput() {
            this.$refs.itemSearch.focus();
        },
        cssClass(index = null, item = null) {
            let classes = [];
            if (this.highlightedItem === index) {
                classes.push('text-white');
                classes.push('bg-primary');
            }

            if (item.disabled) {
                classes.push('text-primary-20 cursor-not-allowed');
            }

            return classes.join(' ')
        },
    },
    mounted() {
        if (this.autoFocus) {
            this.$refs.itemSearch.focus();
        }
        this.setFirstEnabledItem();
    }
}
</script>
